// src/pages/Buyer/Verification.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  VerifiedUser,
  Download,
  FileUpload,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { verificationAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

// ── Helper: get current user from localStorage ────────────────────────────
const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
};

const BuyerVerification = () => {
  const navigate = useNavigate();

  // ── Auto-detect country from user profile ─────────────────────────────────
  const storedUser = getStoredUser();
  const userCountry = storedUser?.country || 'India';
  const defaultTab  = userCountry === 'UAE' ? 1 : 0;

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [country, setCountry] = useState(userCountry === 'UAE' ? 'UAE' : 'India');
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState({});

  // ── Auto-fill from Google / user profile ──────────────────────────────────
  const [formData, setFormData] = useState({
    gst_number: '',
    pan_number: '',
    iec_number: '',
    business_registration_certificate: storedUser?.company_name || '',
    trade_license: '',
    import_permit: '',
    company_registration: storedUser?.company_name || '',
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showReupload, setShowReupload] = useState(false);
  const [reuploadFiles, setReuploadFiles] = useState({});
  const [reuploadSubmitting, setReuploadSubmitting] = useState(false);

  // ── Google user info banner ───────────────────────────────────────────────
  const isGoogleUser = storedUser?.oauth_provider === 'google';

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      setLoading(true);
      const response = await verificationAPI.getStatus();
      const data = response.data;
      setVerificationStatus(data);

      if (data.user_verified) {
        setActiveStep(3); // Already verified by admin
      } else if (data.submission_status === 'submitted') {
        // Pre-fill form with previously submitted values
        setActiveStep(3); // Show pending-review screen (step 3 = VerificationCompleteStep)
        // Also restore the form fields so buyer can see what they submitted
        const india = data.india_verification || {};
        setFormData(prev => ({
          ...prev,
          gst_number: data.gst_number || india.gst_number || '',
          pan_number: data.pan_number || india.pan_number || '',
          iec_number: data.iec_number || india.iec_number || '',
          business_registration_certificate: india.business_registration_certificate || '',
          trade_license:        (data.uae_verification || {}).trade_license        || '',
          import_permit:        (data.uae_verification || {}).import_permit         || '',
          company_registration: (data.uae_verification || {}).company_registration  || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const countries = ['India', 'UAE'];
  const steps = country === 'India' 
    ? ['Business Details', 'Document Upload', 'Review & Submit', 'Verification Complete']
    : ['Company Details', 'Document Upload', 'Review & Submit', 'Verification Complete'];

  const handleFileUpload = (fieldName, event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReuploadSubmit = async () => {
    if (Object.keys(reuploadFiles).length === 0) {
      toast.error('Please select at least one document to upload');
      return;
    }
    setReuploadSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('country', country);
      // Include existing text fields so backend keeps them
      if (formData.gst_number)          fd.append('gst_number',          formData.gst_number);
      if (formData.pan_number)          fd.append('pan_number',          formData.pan_number);
      if (formData.iec_number)          fd.append('iec_number',          formData.iec_number);
      if (formData.trade_license)       fd.append('trade_license',       formData.trade_license);
      if (formData.import_permit)       fd.append('import_permit',       formData.import_permit);
      if (formData.company_registration)fd.append('company_registration',formData.company_registration);
      Object.keys(reuploadFiles).forEach(key => {
        if (reuploadFiles[key]) fd.append(key, reuploadFiles[key]);
      });
      await verificationAPI.submit(fd);
      // If was previously verified, the backend resets verified=false → pending re-review
      const wasVerified = verificationStatus?.user_verified;
      toast.success(
        wasVerified
          ? '✅ Documents updated! Your account is now pending re-verification by admin.'
          : '✅ Documents submitted successfully! Awaiting admin review.'
      );
      setShowReupload(false);
      setReuploadFiles({});
      fetchVerificationStatus();
    } catch (error) {
      console.error('Reupload error:', error);
      toast.error('Failed to upload documents. Please try again.');
    } finally {
      setReuploadSubmitting(false);
    }
  };

  const handleReuploadFileChange = (fieldName, event) => {
    const file = event.target.files[0];
    if (file) setReuploadFiles(prev => ({ ...prev, [fieldName]: file }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const data = new FormData();
      data.append('country', country);
      
      // Add form data based on country
      if (country === 'India') {
        if (formData.gst_number)                       data.append('gst_number', formData.gst_number);
        if (formData.pan_number)                       data.append('pan_number', formData.pan_number);
        if (formData.iec_number)                       data.append('iec_number', formData.iec_number);
        if (formData.business_registration_certificate) data.append('business_registration_certificate', formData.business_registration_certificate);
      } else {
        if (formData.trade_license) {
          data.append('trade_license', formData.trade_license);
        }
        if (formData.import_permit) {
          data.append('import_permit', formData.import_permit);
        }
        if (formData.company_registration) {
          data.append('company_registration', formData.company_registration);
        }
      }
      
      // Add uploaded files
      Object.keys(uploadedFiles).forEach(key => {
        if (uploadedFiles[key]) {
          data.append(key, uploadedFiles[key]);
        }
      });
      
      await verificationAPI.submit(data);
      
      toast.success('Verification submitted successfully!');
      setActiveStep(3);
      fetchVerificationStatus();
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.error || 'Verification submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DocumentUploadStep
            country={country}
            formData={formData}
            uploadedFiles={uploadedFiles}
            onFileUpload={handleFileUpload}
            onChange={handleChange}
          />
        );
      case 1:
        return (
          <ReviewStep
            country={country}
            formData={formData}
            uploadedFiles={uploadedFiles}
          />
        );
      case 2:
        return (
          <SubmissionStep
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        );
      case 3:
        return <VerificationCompleteStep verificationStatus={verificationStatus} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading verification status...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Business Verification
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete verification to start trading on our platform
        </Typography>
      </Box>

      {/* Show complete/pending screen if verified OR already submitted */}
      {(verificationStatus?.user_verified || verificationStatus?.submission_status === 'submitted') ? (
        <>
          <VerificationCompleteStep verificationStatus={verificationStatus} />

          {/* ── Additional Document Upload section ── */}
          <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">Upload / Update Documents</Typography>
                <Typography variant="body2" color="text.secondary">
                  Attach new or updated documents to your verification profile
                </Typography>
              </Box>
              <Button
                variant={showReupload ? 'outlined' : 'contained'}
                startIcon={showReupload ? null : <Add />}
                onClick={() => { setShowReupload(v => !v); setReuploadFiles({}); }}
              >
                {showReupload ? 'Cancel' : 'Add / Update Documents'}
              </Button>
            </Box>

            {showReupload && (
              <Box>
                {verificationStatus?.user_verified && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="medium">
                      ⚠️ Updating documents on a verified account will reset your verification status.
                    </Typography>
                    <Typography variant="body2">
                      Your account will be set to <strong>Pending Re-review</strong> until admin approves the new documents.
                    </Typography>
                  </Alert>
                )}
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select the documents you want to add or replace. Only selected files will be updated.
                </Alert>
                <DocumentUploadStep
                  country={country}
                  formData={formData}
                  uploadedFiles={reuploadFiles}
                  onFileUpload={handleReuploadFileChange}
                  onChange={handleChange}
                />
                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={() => { setShowReupload(false); setReuploadFiles({}); }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={reuploadSubmitting ? <CircularProgress size={20} /> : <FileUpload />}
                    onClick={handleReuploadSubmit}
                    disabled={reuploadSubmitting || Object.keys(reuploadFiles).length === 0}
                  >
                    {reuploadSubmitting ? 'Uploading...' : `Submit ${Object.keys(reuploadFiles).length} Document(s)`}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </>
      ) : (
        <>
          {/* Google profile auto-fill notice */}
          {isGoogleUser && storedUser?.company_name && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Your Google profile was detected.</strong> Some fields have been pre-filled with your profile information.
                Your registered name: <strong>{storedUser.company_name}</strong> &nbsp;|&nbsp; Email: <strong>{storedUser.email}</strong>
              </Typography>
            </Alert>
          )}

          {/* Country Selection */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Select Your Country
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Choose your business registration country to see required documents
            </Typography>
            
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => {
                setActiveTab(newValue);
                setCountry(countries[newValue]);
                setActiveStep(0);
                setUploadedFiles({});
                setFormData({
                  gst_number: '',
                  pan_number: '',
                  iec_number: '',
                  business_registration_certificate: '',
                  trade_license: '',
                  import_permit: '',
                  company_registration: '',
                });
              }}
              sx={{ mb: 3 }}
            >
              {countries.map((countryName) => (
                <Tab key={countryName} label={countryName} />
              ))}
            </Tabs>
            
            <Alert severity="info">
              <Typography variant="body2">
                {country === 'India' 
                  ? 'Indian businesses need: GST Registration Number, PAN Number, IEC (Import Export Code), and Business Registration Certificate'
                  : 'UAE businesses need: Trade License, Import Permit, and Company Registration documents'}
              </Typography>
            </Alert>
          </Paper>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Paper sx={{ p: 3, mb: 3 }}>
            {renderStepContent(activeStep)}
          </Paper>

          {/* Navigation Buttons */}
          {activeStep < 3 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                disabled={activeStep === 0}
                onClick={() => setActiveStep(prev => prev - 1)}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              
              {activeStep === steps.length - 2 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : null}
                >
                  {submitting ? 'Submitting...' : 'Submit Verification'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(prev => prev + 1)}
                  disabled={
                    // Require at least one field filled (number OR file)
                    country === 'India'
                      ? !formData.gst_number && !formData.pan_number && !formData.iec_number && !Object.keys(uploadedFiles).length
                      : !formData.trade_license && !formData.import_permit && !formData.company_registration && !Object.keys(uploadedFiles).length
                  }
                  endIcon={<ArrowForward />}
                >
                  Next
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

const DocumentUploadStep = ({ country, formData, uploadedFiles, onFileUpload, onChange }) => {
  const documents = country === 'India'
    ? [
        {
          title: 'GST Registration Certificate',
          fieldName: 'gst_number',
          fileField: 'gst_document',
          description: 'GSTIN — 15-digit Goods and Services Tax Identification Number',
          placeholder: 'e.g. 22AAAAA0000A1Z5',
          required: true,
        },
        {
          title: 'PAN Card',
          fieldName: 'pan_number',
          fileField: 'pan_document',
          description: 'Permanent Account Number issued by Income Tax Department',
          placeholder: 'e.g. ABCDE1234F',
          required: true,
        },
        {
          title: 'IEC (Import Export Code)',
          fieldName: 'iec_number',
          fileField: 'iec_document',
          description: '10-digit IEC issued by DGFT — mandatory for import/export',
          placeholder: 'e.g. AABBC1234D',
          required: true,
        },
        {
          title: 'Business Registration Certificate',
          fieldName: 'business_registration_certificate',
          fileField: 'brc_document',
          description: 'Certificate of Incorporation or Business Registration (Udyam/MSME)',
          placeholder: 'Enter registration number',
          required: false,
        },
      ]
    : [
        {
          title: 'Trade License',
          fieldName: 'trade_license',
          fileField: 'trade_license_document',
          description: 'Valid UAE Trade License',
          required: true,
        },
        {
          title: 'Import Permit',
          fieldName: 'import_permit',
          fileField: 'import_permit_document',
          description: 'Import permit for fruits',
          required: true,
        },
        {
          title: 'Company Registration',
          fieldName: 'company_registration',
          fileField: 'company_reg_document',
          description: 'Company registration documents',
          required: true,
        },
      ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload Required Documents
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please upload clear scanned copies of the following documents. All documents will be verified by our team.
      </Typography>
      
      <Grid container spacing={3}>
        {documents.map((doc) => (
          <Grid item xs={12} key={doc.fieldName}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {doc.title}
                  {doc.required && (
                    <Typography variant="caption" color="error">
                      (Required)
                    </Typography>
                  )}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {doc.description}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={`${doc.title} Number`}
                      name={doc.fieldName}
                      value={formData[doc.fieldName] || ''}
                      onChange={onChange}
                      size="small"
                      placeholder={doc.placeholder || ''}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <input
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      id={`file-${doc.fileField}`}
                      type="file"
                      onChange={(e) => onFileUpload(doc.fileField, e)}
                    />
                    
                    <label htmlFor={`file-${doc.fileField}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUpload />}
                        fullWidth
                        size="small"
                      >
                        Upload {doc.title}
                      </Button>
                    </label>
                    
                    {uploadedFiles[doc.fileField] && (
                      <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                        ✓ {uploadedFiles[doc.fileField].name}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          • Upload clear, readable scanned copies (PDF, JPG, PNG)
          <br />
          • Maximum file size: 5MB per document
          <br />
          • All documents must be valid and current
          <br />
          • Verification typically takes 24-48 hours
        </Typography>
      </Alert>
    </Box>
  );
};

const ReviewStep = ({ country, formData, uploadedFiles }) => {
  const documents = country === 'India'
    ? [
        { title: 'GST Number',      fieldName: 'gst_number',                       fileField: 'gst_document' },
        { title: 'PAN Number',      fieldName: 'pan_number',                       fileField: 'pan_document' },
        { title: 'IEC Number',      fieldName: 'iec_number',                       fileField: 'iec_document' },
        { title: 'Business Reg.',   fieldName: 'business_registration_certificate', fileField: 'brc_document' },
      ]
    : [
        { title: 'Trade License', fieldName: 'trade_license', fileField: 'trade_license_document' },
        { title: 'Import Permit', fieldName: 'import_permit', fileField: 'import_permit_document' },
        { title: 'Company Registration', fieldName: 'company_registration', fileField: 'company_reg_document' },
      ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Submission
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review all information before submitting for verification.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Document Details
              </Typography>
              
              {documents.map((doc) => (
                <Box key={doc.fieldName} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" color="text.secondary">
                    {doc.title}:
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {formData[doc.fieldName] || 'Not provided'}
                  </Typography>
                  
                  {uploadedFiles[doc.fileField] && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Download fontSize="small" />
                      <Typography variant="caption">
                        {uploadedFiles[doc.fileField].name}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Verification Information
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium" color="text.secondary">
                  Country:
                </Typography>
                <Typography variant="body1">{country}</Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Alert severity="info">
                <Typography variant="body2">
                  After submission:
                  <br />
                  • Our team will review your documents within 24-48 hours
                  <br />
                  • You will receive email notifications about verification status
                  <br />
                  • You can track verification progress in your dashboard
                  <br />
                  • Once verified, you can start making enquiries immediately
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const SubmissionStep = ({ onSubmit, submitting }) => {
  return (
    <Box textAlign="center" py={4}>
      <VerifiedUser sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Ready to Submit?
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
        By submitting your verification documents, you confirm that all information provided is accurate and complete. 
        Our team will review your submission and update your verification status.
      </Typography>
      
      <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
        <Typography variant="body2">
          Please ensure all documents are valid and clearly readable. 
          Incomplete or unclear submissions may delay the verification process.
        </Typography>
      </Alert>
      
      <Button
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
        sx={{ minWidth: 200 }}
      >
        {submitting ? 'Submitting...' : 'Submit for Verification'}
      </Button>
    </Box>
  );
};

const VerificationCompleteStep = ({ verificationStatus }) => {
  const isVerified  = verificationStatus?.user_verified;
  const india       = verificationStatus?.india_verification || {};
  const uae         = verificationStatus?.uae_verification   || {};
  const gst         = verificationStatus?.gst_number || india.gst_number;
  const pan         = verificationStatus?.pan_number || india.pan_number;
  const iec         = verificationStatus?.iec_number || india.iec_number;
  // If verified_at is set but currently NOT verified → was previously approved, now re-submitted
  const wasApproved = !!(india.verified_at || uae.verified_at);
  const isPendingReReview = !isVerified && wasApproved;

  // ── State 1: Fully verified ───────────────────────────────────────────────
  if (isVerified) {
    return (
      <Box textAlign="center" py={4}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight="bold">Verification Approved!</Typography>
        <Alert severity="success" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
          <Typography variant="body1" fontWeight="medium">Account Status: Verified ✓</Typography>
          <Typography variant="body2">
            You can now browse fruits, submit enquiries, and start trading.
          </Typography>
        </Alert>
        {(gst || pan || iec) && (
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>Your KYC Details:</Typography>
            {gst && <Typography variant="body2">GST: {gst}</Typography>}
            {pan && <Typography variant="body2">PAN: {pan}</Typography>}
            {iec && <Typography variant="body2">IEC: {iec}</Typography>}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" size="large"
            onClick={() => window.location.href = '/fruits'} sx={{ minWidth: 200 }}>
            Browse Fruits
          </Button>
          <Button variant="outlined" size="large"
            onClick={() => window.location.href = '/buyer/dashboard'} sx={{ minWidth: 200 }}>
            Go to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  // ── State 2: Updated docs — pending RE-review (was previously approved) ──
  if (isPendingReReview) {
    return (
      <Box textAlign="center" py={4}>
        <VerifiedUser sx={{ fontSize: 80, color: 'info.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight="bold" color="info.main">
          Documents Updated — Pending Re-verification
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
          You've updated your KYC documents. Your account is temporarily on hold while admin
          reviews your new submissions. This usually takes 24–48 hours.
        </Typography>

        <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
          <Typography variant="body1" fontWeight="medium">⏳ Status: Pending Re-verification</Typography>
          <Typography variant="body2">
            • Your previous verification has been reset<br />
            • Updated documents are queued for admin review<br />
            • You will be notified by email once re-verified<br />
            • Trading features are temporarily restricted until re-approved
          </Typography>
        </Alert>

        {(gst || pan || iec) && (
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>Submitted KYC Details:</Typography>
            {gst && <Typography variant="body2">GST: <strong>{gst}</strong></Typography>}
            {pan && <Typography variant="body2">PAN: <strong>{pan}</strong></Typography>}
            {iec && <Typography variant="body2">IEC: <strong>{iec}</strong></Typography>}
          </Alert>
        )}

        <Button variant="contained" size="large"
          onClick={() => window.location.href = '/buyer/dashboard'} sx={{ minWidth: 200 }}>
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  // ── State 3: First-time submission — pending initial review ───────────────
  return (
    <Box textAlign="center" py={4}>
      <VerifiedUser sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom fontWeight="bold">KYC Submitted — Pending Review</Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
        Your verification documents have been submitted. Our team will review them within 24–48 hours.
      </Typography>

      {(gst || pan || iec) && (
        <Alert severity="success" sx={{ maxWidth: 600, mx: 'auto', mb: 3, textAlign: 'left' }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>Submitted KYC Details:</Typography>
          {gst && <Typography variant="body2">GST Number: <strong>{gst}</strong></Typography>}
          {pan && <Typography variant="body2">PAN Number: <strong>{pan}</strong></Typography>}
          {iec && <Typography variant="body2">IEC (Import Export Code): <strong>{iec}</strong></Typography>}
        </Alert>
      )}

      <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
        <Typography variant="body1" fontWeight="medium">Verification Status: Pending Review</Typography>
        <Typography variant="body2">
          • You will receive an email once verification is complete<br />
          • Typical processing time: 24–48 hours<br />
          • You can check status in your dashboard
        </Typography>
      </Alert>

      <Button variant="contained" size="large"
        onClick={() => window.location.href = '/buyer/dashboard'} sx={{ minWidth: 200 }}>
        Go to Dashboard
      </Button>
    </Box>
  );
};

export default BuyerVerification;